import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiChevronDown, FiHeart, FiMinus, FiPlus } from "react-icons/fi";
import productData from "../Datas/productData";
import "../css/productdetails.scss";
import { CartContext } from "./CartContext";
import { formatMoney } from "../utils/commerce";
import ProductCard from "./ProductCard";

const colorValues = {
  "Deep Indigo": "#172539", "Black Rinse": "#171717", "Washed Black": "#333231", Graphite: "#555351",
  "Antique Blue": "#667b8d", "Soft Stone": "#bbb4a9", "Ink Rinse": "#253347", "Clean Black": "#111",
  "Vintage Indigo": "#38536c", "Dark Rinse": "#26394c", Charcoal: "#4b4948", Midnight: "#202a36",
  "Clean Blue": "#64849e", "Light Vintage": "#9eb4c1", Ecru: "#e8e0ce", "Warm Stone": "#c5b9a7",
  "Mid Stone": "#7893a4", "Pale Blue": "#b6cbd5", "Soft Grey": "#969592", "Worn Blue": "#71899b",
  White: "#f5f3ee", Black: "#181818", Cocoa: "#765847", Chalk: "#e7e1d4", "Natural Ecru": "#dfd4bc",
};

function ProductDetail() {
  const { id } = useParams();
  const numericId = /^\d+$/.test(id || "") ? Number(id) : NaN;
  const product = productData.find((item) => item.id === numericId);
  const { addToCart } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setSelectedSize(product?.size?.[0] || "");
    setSelectedColor(product?.colors?.[0] || "");
    setQuantity(1);
    setActiveImage(0);
    setAdded(false);
  }, [product]);

  const related = useMemo(() => {
    if (!product) return [];
    return productData.filter((item) => item.id !== product.id && item.category === product.category).slice(0, 4);
  }, [product]);

  if (!product) {
    return <main className="product-not-found"><p className="eyebrow">404</p><h1>Product not found</h1><p>The style may have moved or is no longer in the collection.</p><Link className="button button--dark" to="/shop">Shop all</Link></main>;
  }

  const handleAdd = () => {
    addToCart(product.id, quantity, selectedSize, selectedColor);
    setAdded(true);
    window.setTimeout(() => setAdded(false), 2200);
  };

  return (
    <main className="product-page">
      <div className="product-page__layout">
        <div className="product-gallery">
          {product.images.map((image, index) => (
            <button className={`product-gallery__item ${activeImage === index ? "is-active" : ""}`} key={image} type="button" onClick={() => setActiveImage(index)}>
              <img src={image} alt={`${product.name}, view ${index + 1}`} />
            </button>
          ))}
          <span className="product-gallery__count">{activeImage + 1} / {product.images.length}</span>
        </div>

        <aside className="product-panel">
          {product.badge && <p className={`product-panel__badge ${product.badge === "FINAL SALE" ? "is-sale" : ""}`}>{product.badge}</p>}
          <h1>{product.name}</h1>
          <div className="product-panel__price">{product.compareAtPrice && <s>{formatMoney(product.compareAtPrice)}</s>}<span>{formatMoney(product.price)}</span></div>
          <p className="product-panel__fit">{product.fitLine}</p>

          <div className="product-option">
            <div className="product-option__head"><span>Choose colour: <strong>{selectedColor}</strong></span></div>
            <div className="colour-options">
              {product.colors.map((color) => <button key={color} type="button" className={selectedColor === color ? "selected" : ""} onClick={() => setSelectedColor(color)} aria-label={`Choose ${color}`}><span style={{ backgroundColor: colorValues[color] || "#8c8178" }} /></button>)}
            </div>
          </div>

          <div className="product-option">
            <div className="product-option__head"><span>Choose size</span><Link to="/fit-guide">Size guide</Link></div>
            <div className="size-options">
              {product.size.map((size) => <button key={size} type="button" className={selectedSize === size ? "selected" : ""} onClick={() => setSelectedSize(size)}>{size}</button>)}
            </div>
          </div>

          <div className="product-buy">
            <div className="quantity-control" aria-label="Quantity">
              <button type="button" onClick={() => setQuantity((value) => Math.max(1, value - 1))} aria-label="Decrease quantity"><FiMinus /></button>
              <span>{quantity}</span>
              <button type="button" onClick={() => setQuantity((value) => Math.min(10, value + 1))} aria-label="Increase quantity"><FiPlus /></button>
            </div>
            <button className="add-button" type="button" onClick={handleAdd}>{added ? "Added to bag" : `Add to cart — ${formatMoney(Number(product.price) * quantity)}`}</button>
            <button className="wish-button" type="button" aria-label="Add to wishlist"><FiHeart /></button>
          </div>
          {added && <p className="added-message" role="status">Added to your bag. <Link to="/cart">View bag</Link></p>}
          <p className="stock-note"><span /> {product.stock < 5 ? `Only ${product.stock} left` : "In stock — ready to dispatch"}</p>

          <div className="product-accordions">
            <details open>
              <summary>Product details <FiChevronDown /></summary>
              <div>{product.description.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}<ul>{product.Features.map((feature) => <li key={feature}>{feature}</li>)}</ul></div>
            </details>
            <details><summary>Sizing & fit <FiChevronDown /></summary><div>{product.fitNotes.map((note) => <p key={note}>{note}</p>)}</div></details>
            <details><summary>Fabric & care <FiChevronDown /></summary><div>{product.fabricDetails.map((detail) => <p key={detail}>{detail}</p>)}</div></details>
            <details><summary>Our factory <FiChevronDown /></summary><div><p>Our denim partners are selected for technical expertise, worker care and traceable processes. Each piece is reviewed for fit, finish and durability before it reaches you.</p></div></details>
            <details><summary>Shipping & returns <FiChevronDown /></summary><div><p>Standard shipping is US$9.99 per order. Eligible full-price items may be returned within 30 days.</p><Link to="/shipping">Read shipping & returns</Link></div></details>
          </div>
        </aside>
      </div>

      <section className="product-promises">
        <div><strong>Fit that works</strong><p>Consistent, considered proportions designed for real wardrobes.</p></div>
        <div><strong>Premium materials</strong><p>Denim chosen for structure, comfort and the way it improves with wear.</p></div>
        <div><strong>Designed with purpose</strong><p>Every detail begins with fit, function and repeat wear in mind.</p></div>
      </section>

      {related.length > 0 && <section className="related-products page-section"><div className="section-heading"><h2>You may also like</h2><Link to={`/shop?category=${encodeURIComponent(product.category)}`}>View all</Link></div><div className="related-products__grid">{related.map((item) => <ProductCard key={item.id} product={item} />)}</div></section>}
    </main>
  );
}

export default ProductDetail;
